import { apiFetch } from "./client";

// Team Types
export interface Team {
  teamId: number;
  teamName: string;
  description?: string;
  artistId: number;
  artistName?: string;
  clickhouseArtistId?: string; // ClickHouse artist ID for secondary database queries
  ownerId: number;
  ownerName?: string;
  ownerUsername?: string; // Username of the team owner
  isActive: boolean;
  teamType: 'artist_team' | 'label_team';
  memberCount?: number;
  myRoles?: string[];
  myPermissions?: string[];
  isOwner?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface TeamMember {
  memberId: number | null; // null for pending invitations
  userId: number | null; // null for pending invitations
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  roles: string[];
  permissions: string[];
  invitationStatus: 'pending' | 'accepted' | 'rejected' | 'revoked';
  joinedAt: string | null; // null for pending invitations
  isActive: boolean;
  expiresAt?: string; // Expiration date for pending invitations
  invitationId?: number; // Present for pending invitations
  invitedAt?: string; // Present for pending invitations
  isPendingInvitation?: boolean; // Flag to identify pending invitations
}

export interface TeamDetails extends Team {
  members: TeamMember[];
}

export interface CreateTeamRequest {
  teamName: string;
  description?: string;
}

export interface UpdateTeamRequest {
  teamName?: string;
  description?: string;
}

export interface InviteMemberRequest {
  email: string;
  roles: string[];
  message?: string;
}

export interface Invitation {
  invitationId: number;
  teamId: number;
  teamName: string;
  artistId: number;
  artistName: string;
  inviterName: string;
  message?: string;
  roles: string[];
  sentAt: string;
  expiresAt: string;
  invitationStatus: 'pending' | 'accepted' | 'rejected' | 'expired' | 'revoked'; // Field name from API response
  invitationToken?: string;
  respondedAt?: string; // When the invitation was accepted/rejected
  teamIsActive?: boolean; // Team active status (true = active, false = inactive/deleted)
}

export interface Role {
  roleId: number;
  roleCode: string;
  roleName: string;
  description?: string;
  roleLevel: number;
  isSystemRole: boolean;
  isActive?: boolean;
  permissions?: string[];
  createdAt?: string;
}

export interface Permission {
  permissionId: number;
  permissionCode: string;
  permissionName: string;
  permissionCategory: string;
  description?: string;
  isActive?: boolean;
  createdAt?: string;
}

export interface UpdateMemberRolesRequest {
  roles: string[];
}

export interface MemberPermissions {
  userId: number;
  memberId: number;
  roles: string[];
  permissions: Array<{
    code: string;
    name: string;
    category: string;
    grantedByRoles: string[];
  }>;
  permissionsByCategory: Record<string, string[]>;
}

export interface ActivityLog {
  activityId: number;
  actionType: string;
  actionCategory: string;
  userId: number;
  userName: string;
  targetUserId?: number;
  targetUserName?: string;
  details: Record<string, any>;
  createdAt: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Team Management APIs
export const createTeam = async (data: CreateTeamRequest): Promise<Team> => {
  const response = await apiFetch('/teams/create', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
};

export const getTeam = async (teamId: number): Promise<TeamDetails> => {
  const response = await apiFetch(`/teams/${teamId}`);
  return response.data;
};

export const getMyTeams = async (artistId?: number): Promise<Team[]> => {
  // Use artist_id as query parameter name (as requested for teams page)
  const url = artistId ? `/teams/my-teams?artist_id=${artistId}` : '/teams/my-teams';
  // Include X-Artist-Id header so API can filter teams by selected artist
  const response = await apiFetch(url);
  return response.data;
};

export const updateTeam = async (teamId: number, data: UpdateTeamRequest): Promise<Team> => {
  const response = await apiFetch(`/teams/${teamId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.data;
};

export const deleteTeam = async (teamId: number): Promise<void> => {
  await apiFetch(`/teams/${teamId}`, {
    method: 'DELETE',
  });
};

// Team Member Management APIs
export const inviteTeamMember = async (
  teamId: number,
  data: InviteMemberRequest
): Promise<{ invitationId: number; email: string; userExists: boolean; userId?: number; invitationToken: string; expiresAt: string; status: string }> => {
  try {
  const response = await apiFetch(`/teams/${teamId}/members/invite`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  } catch (error: any) {
    // If status is 428, throw a special error that can be caught and handled
    if (error?.status === 428 || error?.message?.includes('REACTIVATION_REQUIRED')) {
      const reactivationError = new Error(error?.message || 'User is already registered for this team but is inactive. Reactivation is required.');
      (reactivationError as any).status = 428;
      (reactivationError as any).code = 'REACTIVATION_REQUIRED';
      throw reactivationError;
    }
    throw error;
  }
};

export interface ReactivateMemberRequest {
  email: string;
  roles: string[];
  message?: string;
}

export interface ReactivateMemberResponse {
  memberId: number;
  invitationId: number;
  email: string;
  userId: number;
  invitationToken: string;
  expiresAt: string;
  status: string;
  reactivatedAt: string;
}

export const reactivateTeamMember = async (
  teamId: number,
  data: ReactivateMemberRequest
): Promise<ReactivateMemberResponse> => {
  const response = await apiFetch(`/teams/${teamId}/members/reactivate`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
};

export const removeTeamMember = async (teamId: number, memberId: number): Promise<void> => {
  await apiFetch(`/teams/${teamId}/members/${memberId}`, {
    method: 'DELETE',
  });
};

export const updateMemberRoles = async (
  teamId: number,
  memberId: number,
  data: UpdateMemberRolesRequest
): Promise<TeamMember> => {
  const response = await apiFetch(`/teams/${teamId}/members/${memberId}/roles`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.data;
};

export const getMemberPermissions = async (
  teamId: number,
  memberId: number | null,
  invitationId?: number
): Promise<MemberPermissions> => {
  let url = `/teams/${teamId}/members/${memberId}/permissions`;
  
  // If memberId is null and invitationId is provided, add it as query parameter
  if (memberId === null && invitationId !== undefined) {
    url += `?invitationId=${invitationId}`;
  }
  
  const response = await apiFetch(url);
  return response.data;
};

// Invitation Management APIs
export const getPendingInvitations = async (): Promise<Invitation[]> => {
  const response = await apiFetch('/invitations/pending');
  return response.data;
};

// Get all invitations (pending, accepted, rejected, expired)
export const getAllInvitations = async (): Promise<Invitation[]> => {
  const response = await apiFetch('/invitations');
  return response.data;
};

export const acceptInvitation = async (token: string, createAccount: boolean = false): Promise<{ teamId: number; teamName: string; memberId: number; roles: string[]; joinedAt: string }> => {
  const response = await apiFetch(`/invitations/${token}/accept`, {
    method: 'POST',
    body: JSON.stringify({ createAccount }),
  });
  return response.data;
};

export const rejectInvitation = async (token: string): Promise<void> => {
  await apiFetch(`/invitations/${token}/reject`, {
    method: 'POST',
  });
};

export const revokeInvitation = async (invitationId: number): Promise<void> => {
  await apiFetch(`/invitations/${invitationId}/revoke`, {
    method: 'DELETE',
  });
};

// Role & Permission Management APIs
export const getRoles = async (includePermissions: boolean = false): Promise<Role[]> => {
  const url = includePermissions ? '/roles?includePermissions=true' : '/roles';
  const response = await apiFetch(url);
  return response.data;
};

export const createRole = async (data: {
  roleCode: string;
  roleName: string;
  description?: string;
  roleLevel: number;
  permissions: string[];
}): Promise<Role> => {
  const response = await apiFetch('/roles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
};

export const getPermissions = async (): Promise<Record<string, Permission[]>> => {
  const response = await apiFetch('/permissions');
  return response.data;
};

// Authorization Check APIs
export interface MyPermissionsResponse {
  permissions: string[];
  roles: string[];
  teamIds: number[];
}

export const getMyPermissions = async (artistId: number): Promise<MyPermissionsResponse> => {
  const response = await apiFetch(`/auth/my-permissions?artistId=${artistId}`);
  return response.data;
};

export const checkPermission = async (artistId: number, permission: string): Promise<{ hasPermission: boolean; grantedByRoles: string[]; teamId?: number }> => {
  const response = await apiFetch('/auth/check-permission', {
    method: 'POST',
    body: JSON.stringify({ artistId, permission }),
  });
  return response.data;
};

export const bulkCheckPermissions = async (
  artistId: number,
  permissions: string[]
): Promise<Record<string, boolean>> => {
  const response = await apiFetch('/auth/bulk-check-permissions', {
    method: 'POST',
    body: JSON.stringify({ artistId, permissions }),
  });
  return response.data;
};

// Activity Log APIs
export const getTeamActivity = async (
  teamId: number,
  options?: {
    page?: number;
    limit?: number;
    actionType?: string;
    userId?: number;
    startDate?: string;
    endDate?: string;
  }
): Promise<{ data: ActivityLog[]; pagination: PaginationInfo }> => {
  const params = new URLSearchParams();
  if (options?.page) params.append('page', options.page.toString());
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.actionType) params.append('actionType', options.actionType);
  if (options?.userId) params.append('userId', options.userId.toString());
  if (options?.startDate) params.append('startDate', options.startDate);
  if (options?.endDate) params.append('endDate', options.endDate);

  const url = `/teams/${teamId}/activity${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await apiFetch(url);
  return response;
};

